import React, { useMemo, useState, useEffect } from 'react';
import { useTable, useSortBy, usePagination, Column } from 'react-table';

// Types
import Parameter from '../../../types/Parameter';

// Components
import ReactTable from '../../../component/ReactTable';
import Alert from '../../../component/Alert';
import { getDefaultPageSize } from '../../../helpers/utils';
import SearchInput from '../../../component/SearchInput';

// Read-only properties that cannot be edited
const READ_ONLY_PROPERTIES = ['version', 'copyright', 'created'];

// Simple inline editable cell - always shows input
const EditableCell = ({
    value: initialValue,
    row: { original },
    onCellEdit,
    isAdmin,
}: {
    value: any,
    row: { original: Parameter },
    onCellEdit: Function,
    isAdmin: boolean
}) => {
    const [value, setValue] = useState(initialValue);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    const onBlur = () => {
        if (initialValue !== value) {
            onCellEdit(original.name, value === '' ? null : value);
        }
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const isReadOnly = original.isProperty && READ_ONLY_PROPERTIES.includes(original.name);
    const canEdit = isAdmin && !isReadOnly;

    return (
        <input
            type="text"
            className="form-control form-control-sm"
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            disabled={!canEdit}
            data-lpignore={true}
        />
    );
};

interface ParameterListProps {
    data: Parameter[];
    loadingState: boolean;
    onCellEdit: (paramName: string, newValue: string | null) => void;
    onNewParameter: (newParamName: string, newParamValue: string) => void;
    onDeleteParameter: (paramName: string) => void;
    getUniqueParameterValues: (paramName: string) => string[];
    handleSaveChanges: () => Promise<void>;
    isSaving: boolean;
    hasUnsavedChanges: boolean;
    isAdmin: boolean;
}

const ParameterList: React.FC<ParameterListProps> = ({
    data,
    loadingState,
    onCellEdit,
    onNewParameter,
    onDeleteParameter,
    handleSaveChanges,
    isSaving,
    hasUnsavedChanges,
    isAdmin,
}) => {
    const [newParamName, setNewParamName] = useState('');
    const [newParamValue, setNewParamValue] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) {
            return data;
        }
        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(param =>
            param.name.toLowerCase().includes(lowerSearch) ||
            (param.value && param.value.toLowerCase().includes(lowerSearch))
        );
    }, [data, searchTerm]);

    const handleAddNewParameter = () => {
        if (newParamName.trim() && !data.find(param => param.name === newParamName.trim())) {
            onNewParameter(newParamName.trim(), newParamValue.trim());
            setNewParamName('');
            setNewParamValue('');
            setErrorMessage(null);
        } else if (!newParamName.trim()) {
            setErrorMessage("Parameter name is required.");
        } else {
            setErrorMessage("Parameter name already exists.");
        }
    };

    const handleDelete = (paramName: string) => {
        onDeleteParameter(paramName);
    };

    const tableColumns = useMemo<Column<Parameter>[]>(() => {
        const columns: Column<Parameter>[] = [
            {
                Header: 'Parameter Name',
                accessor: 'name',
                sortType: (rowA, rowB, columnId) => {
                    const a = String(rowA.values[columnId]).toLowerCase();
                    const b = String(rowB.values[columnId]).toLowerCase();
                    if (a > b) return 1;
                    if (b > a) return -1;
                    return 0;
                },
            },
            {
                Header: 'Parameter Value',
                accessor: 'value',
                Cell: (cellProps: any) => (
                    <EditableCell
                        {...cellProps}
                        onCellEdit={onCellEdit}
                        isAdmin={isAdmin}
                    />
                ),
            },
        ];

        // Add delete column if user is admin
        if (isAdmin) {
            columns.push({
                Header: 'Actions',
                id: 'actions',
                className: 'actions',
                disableSortBy: true,
                Cell: ({ row }: any) => {
                    const param = row.original as Parameter;
                    // Only show delete button for parameters (not properties)
                    if (!param.isProperty) {
                        return (
                            <React.Fragment>
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(param.name)}
                                    title="Delete parameter"
                                >
                                    <i className="fal fa-trash-alt fa-fw"></i><span>Delete</span>
                                </button>
                            </React.Fragment>
                        );
                    }
                    return null;
                },
            } as any);
        }

        return columns;
    }, [onCellEdit, isAdmin]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns: tableColumns,
            data: filteredData,
            initialState: {
                pageIndex: 0,
                pageSize: getDefaultPageSize(),
            },
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetSelectedRows: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
        },
        useSortBy,
        usePagination
    );

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-cog fa-fw" aria-hidden="true" /> Parameters
                    <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total parameters and properties count">
                        {searchTerm ? `${filteredData.length} / ${data.length}` : data.length}
                    </span>
                    <i
                        className="collapse-toggle"
                        role="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseParameters"
                        aria-expanded="false"
                        aria-controls="collapseParameters"
                    />
                </div>
                <div id="collapseParameters" className="card-body collapse show">
                    {errorMessage && (
                        <Alert
                            message={errorMessage}
                            heading="Invalid Parameter"
                            variant="alert-danger"
                        />
                    )}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10 d-flex align-items-center">
                            {isAdmin && (
                                <>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm me-2"
                                        style={{ width: '200px', height: '31px' }}
                                        placeholder="Parameter Name"
                                        value={newParamName}
                                        onChange={(e) => setNewParamName(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter' && newParamValue) handleAddNewParameter(); }}
                                        data-lpignore={true}
                                    />
                                    <input
                                        type="text"
                                        className="form-control form-control-sm me-2"
                                        style={{ width: '200px', height: '31px' }}
                                        placeholder="Parameter Value"
                                        value={newParamValue}
                                        onChange={(e) => setNewParamValue(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter' && newParamName) handleAddNewParameter(); }}
                                        data-lpignore={true}
                                    />
                                    <button
                                        className="btn btn-sm btn-success me-2"
                                        onClick={handleAddNewParameter}
                                        disabled={!newParamName || !newParamValue}
                                        title="Create Parameter"
                                    >
                                        <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={handleSaveChanges}
                                        disabled={!hasUnsavedChanges || isSaving || loadingState}
                                        title="Save all changes"
                                    >
                                        <i className="fal fa-save fa-fw"></i><span>Save Changes</span>
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="col col-md-2 d-flex align-items-center">
                            <SearchInput
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClear={() => setSearchTerm('')}
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={filteredData.length}
                        error={null}
                        loading={loadingState}
                    />
                </div>
            </div>
        </div>
    );
};

export default ParameterList;
